import { useCallback, useContext, useEffect } from "react";
import {
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom-v5-compat";
import { GetSessionInfoResponseEnum } from "@b2bportal/auth-api";
import type { RbcPartnerMetadata } from "@b2bportal/rbc-accounts-api";
import { B2B_SESSION_ID_KEY } from "@hopper-b2b/i18n";
import { B2B_PORTAL_AUTH_REDIRECT_TO } from "@hopper-b2b/types";
import {
  PATH_AUTH_INVALID_SESSION,
  PATH_END_SESSION,
  PATH_HOME,
  PATH_INVALID_SESSION,
  PATH_START_SESSION,
} from "@hopper-b2b/utilities";
import { ErrorModal, LazyLoading, Loading } from "@tenant/ui";
import queryStringParser from "query-string";
import { environmentConfig } from "../../../config";
import { startSession } from "../../api/session/startSession";
import { HOPPER_USER_ID_PARAM } from "./constants";
import endSession from "@/api/session/endSession";
import getSession from "@/api/session/getSession";
import { UserContext } from "@/App";
import { useEmitRbcRawEvent, RbcAuthRawEventName } from "@/tracking";
import "./styles.scss";

export const StartSessionComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateSessionInfo } = useContext(UserContext);
  const emitCommonAuth = useEmitRbcRawEvent(RbcAuthRawEventName.CommonAuth);

  const authGenericCodeFlow = useCallback(async () => {
    const redirectToSessionStorageUrl = (url: string) => {
      navigate(url, { replace: true });
      sessionStorage.removeItem(B2B_PORTAL_AUTH_REDIRECT_TO);
    };
    const handleRedirect = (redirectUrlFromStartSession: string | null) => {
      const redirectTo = sessionStorage.getItem(B2B_PORTAL_AUTH_REDIRECT_TO);
      if (redirectUrlFromStartSession) {
        navigate(redirectUrlFromStartSession);
      } else if (redirectTo) {
        redirectToSessionStorageUrl(redirectTo);
      } else navigate(PATH_HOME, { replace: true });
    };

    try {
      const searchParams = new URLSearchParams(location?.search);
      const authToken = searchParams.get(HOPPER_USER_ID_PARAM);

      if (authToken) {
        await startSession(authToken);

        // Fetch session info once to get partner metadata and populate UserContext
        const sessionInfoResponse = await getSession();

        if (
          sessionInfoResponse.GetSessionInfoResponse ===
          GetSessionInfoResponseEnum.Session
        ) {
          // Populate UserContext to prevent ProtectedRoute from fetching session again
          updateSessionInfo?.(sessionInfoResponse.sessionInfo);

          const partnerMetadata = sessionInfoResponse.sessionInfo?.userInfo
            ?.partnerMetadata as RbcPartnerMetadata;

          const tier = partnerMetadata?.tierEn?.toLowerCase();

          // Fire common_auth tracking event on successful authentication
          // Use setTimeout(0) to defer to next event loop tick, ensuring tracking providers
          // have received the session info update before the event fires.
          setTimeout(() => {
            emitCommonAuth({
              properties: {
                accountName: partnerMetadata?.accountNameEn,
                ...(tier ? { tier } : {}),
                ...(partnerMetadata?.provinceCode
                  ? { user_province: partnerMetadata.provinceCode }
                  : {}),
              },
            });
          }, 0);
        }

        handleRedirect(null);
      } else {
        navigate(PATH_INVALID_SESSION, { replace: true });
      }
    } catch (err: any) {
      console.error("Error starting session", err);
      navigate(PATH_INVALID_SESSION, { replace: true });
    }
  }, [navigate, location?.search, emitCommonAuth, updateSessionInfo]);

  useEffect(() => {
    const queryString = queryStringParser.parse(location.search);

    if (queryString) authGenericCodeFlow();
    else navigate(PATH_INVALID_SESSION);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Loading />;
};

const decodeHtmlEntities = (str: string): string => {
  const doc = new DOMParser().parseFromString(str, "text/html");
  return doc.documentElement.textContent || str;
};

const tryRedirect = (redirectUrl: string | undefined): boolean => {
  if (!redirectUrl) return false;
  try {
    const decoded = decodeHtmlEntities(redirectUrl);
    const url = new URL(decoded);
    const allowedHosts = [
      "avionrewards.com",
      "steavionrewards.com",
      "royalbank.com",
      "steroyalbank.com",
    ];
    if (
      allowedHosts.some(
        (host) => url.hostname === host || url.hostname.endsWith("." + host)
      )
    ) {
      window.location.href = decoded;
      return true;
    } else {
      console.error("Invalid redirect url configured");
      return false;
    }
  } catch (e) {
    console.error("Malformed redirect url");
    return false;
  }
};

export const InvalidSessionComponent = () => {
  const navigate = useNavigate();
  const redirectUrl = environmentConfig.redirectURL;

  useEffect(() => {
    tryRedirect(redirectUrl);
  }, [redirectUrl]);

  return (
    <ErrorModal
      open
      title="Invalid session"
      subtitle="Please go back to the home page"
      primaryButtonText="Go home"
      primaryOnClick={() => navigate(PATH_HOME)}
    />
  );
};

export const EndSessionComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectUrl = environmentConfig.redirectURL;
  useEffect(() => {
    const endSessionCall = async () => {
      try {
        await endSession();
        // Clear stored session value so the next login is treated as a new session
        // and re-applies the backend profile language preference.
        localStorage.removeItem(B2B_SESSION_ID_KEY);
        const searchParams = new URLSearchParams(location.search);
        const redirectParam = searchParams.get("redirectUrl");
        if (!tryRedirect(redirectParam || redirectUrl)) {
          navigate(PATH_HOME, { replace: true });
        }
      } catch (err) {
        navigate(PATH_AUTH_INVALID_SESSION, { replace: true });
      }
    };
    endSessionCall();
  }, [navigate, location.search, redirectUrl]);

  return <LazyLoading />;
};

export const AuthModule = () => {
  return (
    <Routes>
      <Route path={PATH_START_SESSION} element={<StartSessionComponent />} />
      <Route
        path={PATH_INVALID_SESSION}
        element={<InvalidSessionComponent />}
      />
      <Route path="invalidsession" element={<InvalidSessionComponent />} />
      <Route path={PATH_END_SESSION} element={<EndSessionComponent />} />
    </Routes>
  );
};
